import { useState, useEffect } from 'react';
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  where,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from './useNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import { Deal, DealData, DealType } from '../types/deal';

export const useDeals = (customerId?: string) => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { createNotification } = useNotifications();
  const { t } = useLanguage();

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        setLoading(true);
        const dealsRef = collection(db, 'deals');
        let dealsQuery;

        if (customerId) {
          dealsQuery = query(
            dealsRef,
            where('customerId', '==', customerId),
            orderBy('date', 'desc')
          );
        } else {
          dealsQuery = query(
            dealsRef,
            orderBy('date', 'desc')
          );
        }

        const snapshot = await getDocs(dealsQuery);
        const dealsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate(),
          followUpDate: doc.data().followUpDate?.toDate(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as Deal));

        setDeals(dealsList);
      } catch (error) {
        console.error('Error fetching deals:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [customerId]);

  const addDeal = async (dealData: Omit<DealData, 'createdBy'>) => {
    try {
      const newDeal = {
        ...dealData,
        createdBy: user?.uid || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'deals'), newDeal);

      // Update the local state with the new deal
      const addedDeal: Deal = {
        id: docRef.id,
        ...dealData,
        createdBy: user?.uid || '',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setDeals(prev => [addedDeal, ...prev]);

      // Értesítés létrehozása
      try {
        if (user?.organizationId) {
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../translations/en')).default.notifications
            : (await import('../translations/hu')).default.notifications;
            
          const typeLabels: Record<DealType, string> = lang === 'hu'
            ? { 'phone': 'Telefonhívás', 'email': 'Email', 'sms': 'SMS' }
            : { 'phone': 'Phone call', 'email': 'Email', 'sms': 'SMS' };
            
          await createNotification({
            type: 'system',
            title: translations.subjects?.dealCreated || (lang === 'hu' ? 'Új interakció rögzítve' : 'New interaction recorded'),
            message: `${typeLabels[dealData.type]} ${lang === 'hu' ? 'típusú interakció rögzítve' : 'interaction recorded'}: ${dealData.description.substring(0, 50)}...`,
            priority: 'low',
            userId: user.id,
            organizationId: user.organizationId,
            createdBy: user.id,
            createdByName: user.displayName,
            actionUrl: '/customers',
            data: { dealId: docRef.id, customerId: dealData.customerId }
          });
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }

      return addedDeal;
    } catch (error) {
      console.error('Error adding deal:', error);
      throw error;
    }
  };

  const updateDeal = async (id: string, dealData: Partial<DealData>) => {
    try {
      const dealRef = doc(db, 'deals', id);

      await updateDoc(dealRef, {
        ...dealData,
        updatedAt: serverTimestamp()
      });

      // Update the local state
      setDeals(prev =>
        prev.map(deal =>
          deal.id === id
            ? { ...deal, ...dealData, updatedAt: new Date() }
            : deal
        )
      );

      // Értesítés létrehozása ha státusz változott
      try {
        if (dealData.status && user?.organizationId) {
          const currentDeal = deals.find(d => d.id === id);
          if (currentDeal) {
            const lang = t('common.language') === 'English' ? 'en' : 'hu';
            const translations = lang === 'en' 
              ? (await import('../translations/en')).default.notifications
              : (await import('../translations/hu')).default.notifications;
              
            const statusLabel = (translations.statuses as any)?.[dealData.status] || dealData.status;
            const priority = dealData.status === 'completed' ? 'medium' : 'low';
            
            await createNotification({
              type: 'system',
              title: translations.subjects?.dealStatusChanged || (lang === 'hu' ? 'Interakció státusz változott' : 'Interaction status changed'),
              message: `${lang === 'hu' ? 'Interakció státusza' : 'Interaction status'}: ${statusLabel}`,
              priority: priority,
              userId: user.id,
              organizationId: user.organizationId,
              createdBy: user.id,
              createdByName: user.displayName,
              actionUrl: '/customers',
              data: { dealId: id, customerId: currentDeal.customerId }
            });
          }
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  };

  const deleteDeal = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'deals', id));

      // Update the local state
      setDeals(prev => prev.filter(deal => deal.id !== id));
    } catch (error) {
      console.error('Error deleting deal:', error);
      throw error;
    }
  };

  return {
    deals,
    loading,
    addDeal,
    updateDeal,
    deleteDeal
  };
}; 